import { DateRangePicker, Input, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { SearchFilter } from '~/components/common';

import { getDateByOffset } from '~/utils/date';

import { DeliveryAgencyEstimatedDeliveryDateLabel } from '../../DeliveryAgencyEstimatedDeliveryDateLabel';

import type {
  TypeDeliveryAgencyOrderFilterOptions,
  TypeDeliveryAgencyOrderFilterValues,
  TypeDeliveryAgencyOrderFilterValuesStatus,
} from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyOrderFilter.module.scss';

const DeliveryAgencyOrderFilter = ({
  options,
  values,
  valuesStatus,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options: TypeDeliveryAgencyOrderFilterOptions;
  values: TypeDeliveryAgencyOrderFilterValues;
  valuesStatus: TypeDeliveryAgencyOrderFilterValuesStatus;
  onChange: (values: Partial<TypeDeliveryAgencyOrderFilterValues>) => void;
  onChangeStatus: (values: Partial<TypeDeliveryAgencyOrderFilterValuesStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  return (
    <SearchFilter formId="delivery-agency-order-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.searchKeywordTypes}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
              onChangeStatus({ keyword: 'default' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder="입력"
            maxLength={50}
            value={values.keyword}
            status={valuesStatus.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
            onFocus={() => onChangeStatus({ keyword: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <DeliveryAgencyEstimatedDeliveryDateLabel />
        <div className={styles['input-container']}>
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            minDate={dayjs().subtract(3, 'month').startOf('month').toDate()}
            maxDate={getDateByOffset(2, 'day')}
            value={values.deliveryDate}
            status={valuesStatus.deliveryDate}
            onChange={(value) => onChange({ deliveryDate: value as { start: Date; end: Date } | null })}
            onFocus={() => onChangeStatus({ deliveryDate: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-order-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          주문 상태
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.orderStatuses}
            value={values.orderStatus}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ orderStatus: value })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-type'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 유형
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.deliveryTypes}
            value={values.deliveryType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ deliveryType: value })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-courier'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송사
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.courierCodes}
            value={values.courier}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ courier: value })}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default DeliveryAgencyOrderFilter;
